import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { sanitizeSocialUrl } from "@/lib/socialMedia";

export interface SocialMediaLink {
  id: string;
  platform: string;
  url: string | null;
  display_order: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useSocialMediaLinks = () => {
  return useQuery({
    queryKey: ["social-media-links"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("social_media_links")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as SocialMediaLink[];
    },
  });
};

export const useAllSocialMediaLinks = () => {
  return useQuery({
    queryKey: ["social-media-links-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("social_media_links")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as SocialMediaLink[];
    },
  });
};

export const useUpdateSocialMediaLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<SocialMediaLink> & { id: string }) => {
      const normalizedUpdates = { ...updates };

      if (typeof normalizedUpdates.url === "string" && typeof normalizedUpdates.platform === "string") {
        normalizedUpdates.url = sanitizeSocialUrl(normalizedUpdates.platform, normalizedUpdates.url);
      }

      const { data, error } = await supabase
        .from("social_media_links")
        .update(normalizedUpdates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["social-media-links"] });
      queryClient.invalidateQueries({ queryKey: ["social-media-links-all"] });
      toast({
        title: "Success",
        description: "Social media link updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
